import { isAxiosError } from 'axios';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { BookingEntity, UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { transferBooking } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import alertToast from 'Util/ToastifyUtils';
import { useCustomerSearch } from 'Views/Components/_HumanWritten/CustomerSelection/Queries/useCustomerSearch';
import { Flex } from 'Views/Components/Flex/Flex';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { AsyncSelectInput } from 'Views/Components/New/AsyncSelectInput';
import { TextArea } from 'Views/Components/New/TextArea';

const REASON_MAX_LENGTH = 100;

interface TransferBookingModalProps {
	booking: BookingEntity;
	transferBoth: boolean;
	onClose: () => void;
	onTransferred: () => void;
}

export function TransferBookingModal({ booking, transferBoth, onClose, onTransferred }: TransferBookingModalProps) {
	const { loadOptions } = useCustomerSearch();
	const [selectedCustomer, setSelectedCustomer] = useState<UserEntity | null>(null);
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [conditionsExpanded, setConditionsExpanded] = useState(false);

	const trimmedReason = reason.trim();
	const isValid = !!selectedCustomer && trimmedReason.length > 0;
	const isLocked = error !== null;
	const hasLinkedLeg = !!(booking.returnBooking ?? booking.returnBookingFor);
	const showDesyncHint = hasLinkedLeg && !booking.desynced && !transferBoth;

	const onConfirm = async () => {
		if (!isValid || submitting || isLocked) {
			return;
		}
		setSubmitting(true);
		try {
			await transferBooking({
				bookingId: booking.id,
				toUserId: selectedCustomer.id,
				reason: trimmedReason,
				transferBoth,
			});
			alertToast('Booking transferred', 'success');
			onTransferred();
			onClose();
		} catch (e) {
			const message =
				isAxiosError(e) && typeof e.response?.data === 'string' && e.response.data.length > 0
					? e.response.data
					: 'This booking cannot be transferred right now.';
			setError(message);
			setSubmitting(false);
		}
	};

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>Transfer booking</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				<Flex direction="col" gap="sm">
					{showDesyncHint && (
						<p className="text-sm text-gray-600">
							This ticket will become desynced from its linked ticket.
						</p>
					)}
					<div data-test-id="transfer-booking-customer-select">
						<AsyncSelectInput
							label="Transfer to customer"
							required
							placeholder="Search by name, email or phone"
							loadOptions={loadOptions}
							defaultOptions
							cacheOptions
							isClearable
							isDisabled={isLocked}
							value={selectedCustomer}
							onChange={(option: any) => setSelectedCustomer((option as UserEntity) ?? null)}
							getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} (${option.email})`}
							getOptionValue={(option: any) => option.id}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<TextArea
							label="Reason"
							required
							placeholder="Reason for transfer"
							maxLength={REASON_MAX_LENGTH}
							disabled={isLocked}
							value={reason}
							onChange={e => setReason(e.target.value)}
							data-test-id="transfer-booking-reason"
						/>
						<p className="!text-xs text-slate-500">The customer will see this in their transfer email.</p>
					</div>
					<div>
						<button
							type="button"
							onClick={() => setConditionsExpanded(prev => !prev)}
							className="flex items-center gap-1 !text-sm text-gray-700 hover:text-gray-900"
							aria-expanded={conditionsExpanded}
							aria-controls="transfer-conditions-content"
							data-test-id="transfer-conditions-toggle"
						>
							<Icon name={conditionsExpanded ? 'expand_less' : 'expand_more'} useMaterialIcons />
							<span>Things to know about transfers</span>
						</button>
						{conditionsExpanded && (
							<div
								id="transfer-conditions-content"
								className="mt-2 text-sm text-gray-600 flex flex-col gap-2"
							>
								<p>Refunds from any future cancellation go back to whoever originally paid.</p>
								<div>
									<p>Transfers are not available when:</p>
									<ul className="pl-5 list-disc">
										<li>The booking is invoiced or awaiting invoice payment</li>
										<li>The booking is checked in</li>
										<li>
											<FormattedMessage
												id="transfer-booking__restriction-departed__label"
												defaultMessage="The trip has already departed"
											/>
										</li>
										<li>
											The trip is closed for the booking&apos;s category (passenger, vehicle, or
											freight)
										</li>
									</ul>
								</div>
							</div>
						)}
					</div>
					{isLocked && (
						<MessageBox status="error" noBottomMargin icon="error_outline" title="Transfer not available">
							{error}
						</MessageBox>
					)}
				</Flex>
			</NewModal.Body>
			<NewModal.Footer>
				{isLocked ? (
					<NewModal.CancelButton onClick={onClose}>Close</NewModal.CancelButton>
				) : (
					<>
						<NewModal.CancelButton onClick={onClose} disabled={submitting}>
							Cancel
						</NewModal.CancelButton>
						<NewModal.ConfirmButton
							onClick={onConfirm}
							disabled={!isValid || submitting}
							buttonProps={{ 'data-test-id': 'transfer-booking-confirm' } as any}
						>
							{submitting ? 'Transferring…' : 'Confirm transfer'}
						</NewModal.ConfirmButton>
					</>
				)}
			</NewModal.Footer>
		</>
	);
}

export function transferBookingModal(booking: BookingEntity, transferBoth: boolean, onTransferred: () => void) {
	return new Promise<void>(resolve => {
		const onClose = () => {
			store.modal.hide();
			resolve();
		};

		store.modal?.show(
			'transfer booking',
			<TransferBookingModal
				booking={booking}
				transferBoth={transferBoth}
				onClose={onClose}
				onTransferred={onTransferred}
			/>,
		);
	});
}
